import React from 'react';
import { 
  AlertTriangle, 
  Calendar, 
  User, 
  MessageSquare, 
  Clock,
  CheckCircle,
  XCircle,
  Eye
} from 'lucide-react';

const SignalementCard = ({ signalement, onView, onUpdateStatus }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (statut) => {
    const colors = {
      'EN_ATTENTE': 'bg-orange-100 text-orange-700 border-orange-200',
      'EN_COURS': 'bg-blue-100 text-blue-700 border-blue-200',
      'RESOLU': 'bg-green-100 text-green-700 border-green-200',
      'REJETE': 'bg-red-100 text-red-700 border-red-200'
    };
    return colors[statut] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getStatusIcon = (statut) => {
    const icons = {
      'EN_ATTENTE': Clock,
      'EN_COURS': AlertTriangle,
      'RESOLU': CheckCircle,
      'REJETE': XCircle
    };
    return icons[statut] || Clock;
  };

  const getStatusLabel = (statut) => {
    const labels = {
      'EN_ATTENTE': 'En attente',
      'EN_COURS': 'En cours',
      'RESOLU': 'Résolu',
      'REJETE': 'Rejeté'
    };
    return labels[statut] || statut;
  };

  const getTypeLabel = (type) => {
    const labels = {
      'CONTENU_INAPPROPRIE': 'Contenu inapproprié',
      'SPAM': 'Spam',
      'HARCELEMENT': 'Harcèlement',
      'FAUSSE_INFORMATION': 'Fausse information',
      'AUTRE': 'Autre'
    };
    return labels[type] || type;
  };

  const StatusIcon = getStatusIcon(signalement.statut);

  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100">
      {/* Header */}
      <div className="p-4 bg-gradient-to-r from-[#FF6B00] to-[#437011] text-white">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <AlertTriangle size={20} />
            <span className="font-semibold">Signalement #{signalement.id}</span>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(signalement.statut)} bg-white`}>
            <StatusIcon size={14} className="inline mr-1" />
            {getStatusLabel(signalement.statut)}
          </span>
        </div>
        <p className="text-sm opacity-90">{getTypeLabel(signalement.type_signalement)}</p>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Utilisateur */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
            {signalement.utilisateur?.first_name?.[0] || signalement.utilisateur?.username?.[0] || 'U'}
          </div>
          <div className="flex-1">
            <p className="font-medium text-gray-900 text-sm">
              {signalement.utilisateur?.first_name && signalement.utilisateur?.last_name
                ? `${signalement.utilisateur.first_name} ${signalement.utilisateur.last_name}`
                : signalement.utilisateur?.username || 'Utilisateur'}
            </p>
            <p className="text-xs text-gray-500">{signalement.utilisateur?.email}</p>
          </div>
        </div>

        {/* Publication preview */}
        {signalement.publication_preview && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <MessageSquare size={16} className="text-gray-400 mt-1" />
              <p className="text-sm text-gray-700 line-clamp-2">
                {signalement.publication_preview}
              </p>
            </div>
          </div>
        )}

        {/* Date */}
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Calendar size={14} />
          <span>Signalé le {formatDate(signalement.created_at)}</span>
        </div>

        {/* Traité par */}
        {signalement.traite_par && (
          <div className="flex items-center gap-2 text-xs text-gray-600 bg-blue-50 rounded-lg p-2">
            <User size={14} className="text-blue-600" />
            <span>
              Traité par: {signalement.traite_par.first_name} {signalement.traite_par.last_name}
            </span>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="p-4 bg-gray-50 border-t border-gray-100 flex gap-2">
        <button
          onClick={() => onView(signalement)}
          className="flex-1 px-4 py-2 bg-gradient-to-r from-[#FF6B00] to-[#e55d00] text-white rounded-lg hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 font-medium"
        >
          <Eye size={16} />
          Voir détails
        </button>
      </div>
    </div>
  );
};

export default SignalementCard;
